import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { useBasePath } from '@/lib/paths'
import { formatDate } from '@/lib/utils'
import { BusinessSummary } from '@/types'
import { Briefcase, Building2, Plus } from 'lucide-react'

type Props = {
  businesses: BusinessSummary[]
  search: string
  pagination: PaginationMeta
}

export default function BusinessesIndex({ businesses, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title="Businesses" breadcrumbs={[{ label: 'Businesses' }]}>
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Businesses</h1>
          <p className="text-sm text-muted-foreground">Manage your businesses</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/formations/new`}>
                <Building2 className="w-3.5 h-3.5 mr-1.5" />
                New Formation
              </Link>
            </Button>
            <Button asChild size="sm">
              <Link href={`${basePath}/businesses/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Business
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search businesses..." />
        </div>

        {businesses.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Briefcase className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No businesses yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first business</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/businesses/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Get Started
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {businesses.map((business, i) => (
              <Link
                key={business.id}
                href={`${basePath}/businesses/${business.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Briefcase className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{business.name}</div>
                    <div className="text-xs text-muted-foreground">{business.email}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-4">{formatDate(business.created_at)}</div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
